// src/lib/cookie.ts
import { cookies as rscCookies, headers as rscHeaders } from "@stackframe/stack-sc/force-react-server";
import { isBrowserLike } from "@stackframe/stack-shared/dist/utils/env";
import { StackAssertionError } from "@stackframe/stack-shared/dist/utils/errors";
import Cookies from "js-cookie";
import { calculatePKCECodeChallenge, generateRandomCodeVerifier, generateRandomState } from "oauth4webapi";
function ensureClient() {
  if (!isBrowserLike()) {
    throw new Error("cookieClient functions can only be called in a browser environment, yet window is undefined");
  }
}
async function createPlaceholderCookieHelper() {
  function throwError() {
    throw new StackAssertionError("Throwing cookie helper is just a placeholder. This should never be called");
  }
  return {
    get: throwError,
    getAll: throwError,
    set: throwError,
    setOrDelete: throwError,
    delete: throwError
  };
}
async function createCookieHelper() {
  if (isBrowserLike()) {
    return createBrowserCookieHelper();
  } else {
    return createNextCookieHelper(
      await rscCookies(),
      await rscHeaders()
    );
  }
}
function createBrowserCookieHelper() {
  return {
    get: getCookieClient,
    getAll: getAllCookiesClient,
    set: setCookieClient,
    setOrDelete: setOrDeleteCookieClient,
    delete: deleteCookieClient
  };
}
function handleCookieError(e, options) {
  if (e instanceof Error && e.message.includes("Cookies can only be modified in")) {
    if (options.noOpIfServerComponent) {
    } else {
      throw new StackAssertionError("Attempted to set cookie in server component. Pass { noOpIfServerComponent: true } in the options of Stack's cookie functions if this is intentional and you want to ignore this error. Read more: https://nextjs.org/docs/app/api-reference/functions/cookies#options");
    }
  } else {
    throw e;
  }
}
function createNextCookieHelper(rscCookiesAwaited, rscHeadersAwaited) {
  const cookieHelper = {
    get: (name) => {
      const all = cookieHelper.getAll();
      return all[name] ?? null;
    },
    getAll: () => {
      try {
        rscCookiesAwaited.set("stack-is-https", "true", { secure: true, expires: new Date(Date.now() + 1e3 * 60 * 60 * 24 * 365) });
      } catch (e) {
        if (typeof e === "object" && e !== null && "message" in e && typeof e.message === "string" && e.message.includes("Cookies can only be modified in a Server Action or Route Handler")) {
        } else {
          throw e;
        }
      }
      const all = rscCookiesAwaited.getAll();
      return all.reduce((acc, entry) => {
        acc[entry.name] = entry.value;
        return acc;
      }, {});
    },
    set: (name, value, options) => {
      const isSecureCookie = determineSecureFromServerContext(rscCookiesAwaited, rscHeadersAwaited);
      try {
        rscCookiesAwaited.set(name, value, {
          secure: isSecureCookie,
          maxAge: options.maxAge === "session" ? void 0 : options.maxAge,
          domain: options.domain,
          sameSite: "lax"
        });
      } catch (e) {
        handleCookieError(e, options);
      }
    },
    setOrDelete(name, value, options) {
      if (value === null) {
        this.delete(name, options);
      } else {
        this.set(name, value, options);
      }
    },
    delete(name, options) {
      try {
        if (options.domain !== void 0) {
          rscCookiesAwaited.delete({ name, domain: options.domain });
        } else {
          rscCookiesAwaited.delete(name);
        }
      } catch (e) {
        handleCookieError(e, options);
      }
    }
  };
  return cookieHelper;
}
function getCookieClient(name) {
  const all = getAllCookiesClient();
  return all[name] ?? null;
}
function getAllCookiesClient() {
  ensureClient();
  Cookies.set("stack-is-https", "true", { secure: true, expires: new Date(Date.now() + 1e3 * 60 * 60 * 24 * 365) });
  return Cookies.get();
}
async function getCookie(name) {
  const cookieHelper = await createCookieHelper();
  return cookieHelper.get(name);
}
async function isSecure() {
  if (isBrowserLike()) {
    return determineSecureFromClientContext();
  }
  return determineSecureFromServerContext(await rscCookies(), await rscHeaders());
  return false;
}
function determineSecureFromClientContext() {
  return typeof window !== "undefined" && window.location.protocol === "https:";
}
function determineSecureFromServerContext(cookies, headers) {
  return cookies.has("stack-is-https") || headers.get("x-forwarded-proto") === "https";
}
var _shouldSetPartitionedClientCache = void 0;
function shouldSetPartitionedClient() {
  return _shouldSetPartitionedClientCache ??= _internalShouldSetPartitionedClient();
}
function _internalShouldSetPartitionedClient() {
  ensureClient();
  if (!determineSecureFromClientContext()) {
    return false;
  }
  const cookie1Name = "__Host-stack-temporary-chips-test-" + Math.random().toString(36).substring(2, 15);
  document.cookie = `${cookie1Name}=value1; Secure; path=/`;
  const cookies1 = document.cookie.split("; ");
  document.cookie = `${cookie1Name}=delete1; Secure; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;`;
  if (cookies1.some((c) => c.startsWith(cookie1Name + "="))) {
    return false;
  }
  const cookie2Name = "__Host-stack-temporary-chips-test-" + Math.random().toString(36).substring(2, 15);
  document.cookie = `${cookie2Name}=delete1; Secure; SameSite=None; Partitioned; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
  document.cookie = `${cookie2Name}=delete2; Secure; SameSite=None; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
  document.cookie = `${cookie2Name}=set1; Secure; SameSite=None; Partitioned; path=/`;
  document.cookie = `${cookie2Name}=set2; Secure; SameSite=None; path=/`;
  const cookies2 = document.cookie.split("; ");
  const numberOfCookiesWithThisName = cookies2.filter((c) => c.startsWith(cookie2Name + "=")).length;
  document.cookie = `${cookie2Name}=delete3; Secure; SameSite=None; Partitioned; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
  document.cookie = `${cookie2Name}=delete4; Secure; SameSite=None; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
  return numberOfCookiesWithThisName === 2;
}
function setCookieClientInternal(name, value, options) {
  const secure = options.secure ?? determineSecureFromClientContext();
  const partitioned = shouldSetPartitionedClient();
  Cookies.set(name, value, {
    expires: options.maxAge === "session" ? void 0 : new Date(Date.now() + options.maxAge * 1e3),
    domain: options.domain,
    secure,
    sameSite: "Lax",
    ...partitioned ? {
      partitioned,
      sameSite: "None"
    } : {}
  });
}
function deleteCookieClientInternal(name, options) {
  for (const partitioned of [true, false]) {
    if (options.domain !== void 0) {
      Cookies.remove(name, { domain: options.domain, secure: determineSecureFromClientContext(), partitioned });
    }
    Cookies.remove(name, { secure: determineSecureFromClientContext(), partitioned });
  }
}
function setOrDeleteCookieClient(name, value, options) {
  ensureClient();
  if (value === null) {
    deleteCookieClientInternal(name, options);
  } else {
    setCookieClientInternal(name, value, options);
  }
}
async function setOrDeleteCookie(name, value, options) {
  const cookieHelper = await createCookieHelper();
  cookieHelper.setOrDelete(name, value, options);
}
function deleteCookieClient(name, options) {
  ensureClient();
  deleteCookieClientInternal(name, options);
}
async function deleteCookie(name, options) {
  const cookieHelper = await createCookieHelper();
  cookieHelper.delete(name, options);
}
function setCookieClient(name, value, options) {
  ensureClient();
  setCookieClientInternal(name, value, options);
}
async function setCookie(name, value, options) {
  const cookieHelper = await createCookieHelper();
  cookieHelper.set(name, value, options);
}
async function saveVerifierAndState() {
  const codeVerifier = generateRandomCodeVerifier();
  const codeChallenge = await calculatePKCECodeChallenge(codeVerifier);
  const state = generateRandomState();
  await setCookie("stack-oauth-outer-" + state, codeVerifier, { maxAge: 60 * 60 });
  return {
    codeChallenge,
    state
  };
}
function consumeVerifierAndStateCookie(state) {
  ensureClient();
  const cookieName = "stack-oauth-outer-" + state;
  const codeVerifier = getCookieClient(cookieName);
  if (!codeVerifier) {
    return null;
  }
  deleteCookieClient(cookieName, {});
  return {
    codeVerifier
  };
}
export {
  consumeVerifierAndStateCookie,
  createBrowserCookieHelper,
  createCookieHelper,
  createPlaceholderCookieHelper,
  deleteCookie,
  deleteCookieClient,
  getAllCookiesClient,
  getCookie,
  getCookieClient,
  isSecure,
  saveVerifierAndState,
  setCookie,
  setCookieClient,
  setOrDeleteCookie,
  setOrDeleteCookieClient
};
//# sourceMappingURL=cookie.js.map